// The 26 check locations. One list for the website checker, the 3D globe and
// the status pages. `code` is the region code the API returns. Core probes
// (eu, in) run every monitor type; the other 24 run HTTP and keyword
// checks only. The Ashburn core probe (`us`) was retired on 2026-11-05. The free plan's location (`eu-free`) is not one of the 26: it
// has its own entry below and is named "Nuremberg (free)".

import { FREE_PLAN, FREE_REGION_CODE, freeLocationLabel } from '@/lib/free-plan'

export type Continent = 'Europe' | 'North America' | 'South America' | 'Asia' | 'Australia' | 'Africa'

export type CheckLocation = {
  code: string
  city: string
  country: string
  flag: string
  continent: Continent
  lat: number
  lon: number
  core: boolean
}

export const CONTINENTS: readonly Continent[] = ['Europe', 'North America', 'South America', 'Asia', 'Australia', 'Africa']

export const LOCATIONS: readonly CheckLocation[] = [
  { code: 'eu', city: 'Nuremberg', country: 'Germany', flag: '🇩🇪', continent: 'Europe', lat: 49.45, lon: 11.08, core: true },
  { code: 'in', city: 'Hyderabad', country: 'India', flag: '🇮🇳', continent: 'Asia', lat: 17.39, lon: 78.49, core: true },
  { code: 'lhr', city: 'London', country: 'United Kingdom', flag: '🇬🇧', continent: 'Europe', lat: 51.51, lon: -0.13, core: false },
  { code: 'cdg', city: 'Paris', country: 'France', flag: '🇫🇷', continent: 'Europe', lat: 48.86, lon: 2.35, core: false },
  { code: 'waw', city: 'Warsaw', country: 'Poland', flag: '🇵🇱', continent: 'Europe', lat: 52.23, lon: 21.01, core: false },
  { code: 'yyz', city: 'Toronto', country: 'Canada', flag: '🇨🇦', continent: 'North America', lat: 43.65, lon: -79.38, core: false },
  { code: 'sjc', city: 'San Jose', country: 'United States', flag: '🇺🇸', continent: 'North America', lat: 37.34, lon: -121.89, core: false },
  { code: 'pdx', city: 'Seattle', country: 'United States', flag: '🇺🇸', continent: 'North America', lat: 47.61, lon: -122.33, core: false },
  { code: 'gru', city: 'São Paulo', country: 'Brazil', flag: '🇧🇷', continent: 'South America', lat: -23.55, lon: -46.63, core: false },
  { code: 'gig', city: 'Rio de Janeiro', country: 'Brazil', flag: '🇧🇷', continent: 'South America', lat: -22.91, lon: -43.17, core: false },
  { code: 'bom', city: 'Mumbai', country: 'India', flag: '🇮🇳', continent: 'Asia', lat: 19.08, lon: 72.88, core: false },
  { code: 'sin', city: 'Singapore', country: 'Singapore', flag: '🇸🇬', continent: 'Asia', lat: 1.35, lon: 103.82, core: false },
  { code: 'cgk', city: 'Jakarta', country: 'Indonesia', flag: '🇮🇩', continent: 'Asia', lat: -6.21, lon: 106.85, core: false },
  { code: 'nrt', city: 'Tokyo', country: 'Japan', flag: '🇯🇵', continent: 'Asia', lat: 35.68, lon: 139.69, core: false },
  { code: 'icn', city: 'Seoul', country: 'South Korea', flag: '🇰🇷', continent: 'Asia', lat: 37.57, lon: 126.98, core: false },
  { code: 'doh', city: 'Doha', country: 'Qatar', flag: '🇶🇦', continent: 'Asia', lat: 25.29, lon: 51.53, core: false },
  { code: 'tlv', city: 'Tel Aviv', country: 'Israel', flag: '🇮🇱', continent: 'Asia', lat: 32.09, lon: 34.78, core: false },
  { code: 'syd', city: 'Sydney', country: 'Australia', flag: '🇦🇺', continent: 'Australia', lat: -33.87, lon: 151.21, core: false },
  { code: 'mel', city: 'Melbourne', country: 'Australia', flag: '🇦🇺', continent: 'Australia', lat: -37.81, lon: 144.96, core: false },
  { code: 'jnb', city: 'Johannesburg', country: 'South Africa', flag: '🇿🇦', continent: 'Africa', lat: -26.2, lon: 28.05, core: false },
  { code: 'cpt', city: 'Cape Town', country: 'South Africa', flag: '🇿🇦', continent: 'Africa', lat: -33.92, lon: 18.42, core: false },
  { code: 'yul', city: 'Montréal', country: 'Canada', flag: '🇨🇦', continent: 'North America', lat: 45.5, lon: -73.57, core: false },
  { code: 'dfw', city: 'Dallas', country: 'United States', flag: '🇺🇸', continent: 'North America', lat: 32.78, lon: -96.8, core: false },
  { code: 'ord', city: 'Chicago', country: 'United States', flag: '🇺🇸', continent: 'North America', lat: 41.88, lon: -87.63, core: false },
  { code: 'mxp', city: 'Milan', country: 'Italy', flag: '🇮🇹', continent: 'Europe', lat: 45.46, lon: 9.19, core: false },
  { code: 'del', city: 'Delhi', country: 'India', flag: '🇮🇳', continent: 'Asia', lat: 28.61, lon: 77.21, core: false },
]

/**
 * The 12 areas of two nearby locations each (edge/locations.json in the probe
 * repo). Two areas pair an edge city with the core location next to it (Europe Central and India).
 * North America East had the Ashburn core probe until 2026-11-05; it is two Cloudflare cities now.
 */
export const AREAS: readonly { name: string; codes: readonly string[] }[] = [
  { name: 'North America East', codes: ['yyz', 'yul'] },
  { name: 'US Central', codes: ['dfw', 'ord'] },
  { name: 'North America West', codes: ['sjc', 'pdx'] },
  { name: 'South America', codes: ['gru', 'gig'] },
  { name: 'Europe West', codes: ['lhr', 'cdg'] },
  { name: 'Europe Central', codes: ['eu', 'waw', 'mxp'] },
  { name: 'Middle East', codes: ['doh', 'tlv'] },
  { name: 'Africa', codes: ['jnb', 'cpt'] },
  { name: 'India', codes: ['in', 'bom', 'del'] },
  { name: 'Southeast Asia', codes: ['sin', 'cgk'] },
  { name: 'East Asia', codes: ['nrt', 'icn'] },
  { name: 'Australia', codes: ['syd', 'mel'] },
]

/** Where free checks run (region `eu-free`). Kept out of `LOCATIONS`, which counts the 26. */
export const FREE_LOCATION: CheckLocation = {
  code: FREE_REGION_CODE,
  city: freeLocationLabel(FREE_PLAN.location),
  country: 'Germany',
  flag: '🇩🇪',
  continent: 'Europe',
  lat: 49.45,
  lon: 11.08,
  core: false,
}

/**
 * A location that no longer checks. Old results, charts and notices can still carry its code, so it keeps
 * its label here. It is never in `LOCATIONS`, so it is never counted, drawn on the globe or listed as a choice.
 */
export const RETIRED_LOCATIONS: readonly CheckLocation[] = [
  { code: 'us', city: 'Ashburn', country: 'United States', flag: '🇺🇸', continent: 'North America', lat: 39.04, lon: -77.49, core: true },
]

/** True for a code of a retired location (also its older region name `us-west-2`). */
export function isRetiredLocation(code: string): boolean {
  return code === 'us-west-2' || RETIRED_LOCATIONS.some((l) => l.code === code)
}

const BY_CODE: ReadonlyMap<string, CheckLocation> = new Map([
  ...LOCATIONS.map((l) => [l.code, l] as const),
  ...RETIRED_LOCATIONS.map((l) => [l.code, l] as const),
  // Older region names the API may still return.
  ['europe', LOCATIONS.find((l) => l.code === 'eu')!],
  ['asia', LOCATIONS.find((l) => l.code === 'in')!],
  [FREE_REGION_CODE, FREE_LOCATION],
])

export function locationByCode(code: string): CheckLocation | undefined {
  return BY_CODE.get(code)
}

/** The name shown for a region code: "Nuremberg (free)" for the free location, else the code in upper case ("EU"). */
export function regionLabel(code: string): string {
  return code === FREE_REGION_CODE ? FREE_LOCATION.city : code.toUpperCase()
}
